import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, CheckCircle2, ClipboardList, Loader2, Phone, XCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';

const STATUS = {
  new: { label: 'Nueva', className: 'text-blue-300 border-blue-400/25 bg-blue-400/10' },
  contacted: { label: 'En conversación', className: 'text-amber-300 border-amber-400/25 bg-amber-400/10' },
  quoted: { label: 'Presupuesto enviado', className: 'text-purple-300 border-purple-400/25 bg-purple-400/10' },
  confirmed: { label: 'Confirmada', className: 'text-emerald-300 border-emerald-400/25 bg-emerald-400/10' },
  declined: { label: 'Descartada', className: 'text-red-300 border-red-400/25 bg-red-400/10' },
};

const dateLabel = (date) => new Intl.DateTimeFormat('es-AR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(date));

export default function AdminQuoteRequests() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('active');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`/${slug}/api/quotes`);
      setRequests(data.requests || []);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudieron cargar las solicitudes.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const visibleRequests = useMemo(() => requests.filter((request) => (
    filter === 'active' ? !['confirmed', 'declined'].includes(request.status) : request.status === filter
  )), [requests, filter]);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      const { data } = await axios.patch(`/${slug}/api/quotes/${id}`, { status });
      setRequests((current) => current.map((request) => request._id === id ? data.request : request));
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo actualizar la solicitud.');
    } finally {
      setBusyId('');
    }
  };

  const convert = async (request) => {
    if (!window.confirm(`¿Crear una reserva privada para ${request.contactName} el ${dateLabel(request.requestedDate)}? La fecha quedará bloqueada.`)) return;
    setBusyId(request._id);
    try {
      const { data } = await axios.post(`/${slug}/api/quotes/${request._id}/convert`);
      navigate(`/${slug}/admin/events/edit/${data.event._id}`);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo crear la reserva.');
      setBusyId('');
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] pb-20 text-white">
      <div className="mx-auto max-w-6xl px-4 pt-10 sm:px-6">
        <div className="flex flex-col justify-between gap-6 border-b border-white/10 pb-7 sm:flex-row sm:items-end">
          <div>
            <p className="mb-3 text-[11px] font-black uppercase tracking-[2px] text-primary-orange">Salón de fiestas</p>
            <h1 className="text-4xl font-black sm:text-5xl">Solicitudes de presupuesto</h1>
            <p className="mt-3 text-sm text-white/55">Convertí una consulta confirmada en reserva para bloquear la fecha en la agenda.</p>
          </div>
          <div className="flex flex-wrap gap-2">{[['active', 'Abiertas'], ['confirmed', 'Confirmadas'], ['declined', 'Descartadas']].map(([value, label]) => <button key={value} type="button" onClick={() => setFilter(value)} className={`h-9 px-3 text-xs font-bold ${filter === value ? 'bg-primary-orange text-white' : 'border border-white/15 text-white/60 hover:bg-white/5'}`}>{label}</button>)}</div>
        </div>

        {error ? <p className="mt-5 text-sm font-bold text-red-300">{error}</p> : null}
        {loading ? <div className="flex min-h-[320px] items-center justify-center text-primary-orange"><Loader2 className="animate-spin" size={32} /></div> : null}
        {!loading && visibleRequests.length === 0 ? <div className="border border-dashed border-white/15 py-16 text-center"><ClipboardList className="mx-auto text-white/25" size={34} /><p className="mt-4 text-sm font-bold text-white/45">No hay solicitudes en esta vista.</p></div> : null}

        <div className="mt-7 space-y-4">
          {visibleRequests.map((request) => {
            const status = STATUS[request.status] || STATUS.new;
            const isBusy = busyId === request._id;
            return <article key={request._id} className="border border-white/10 bg-[#111111] p-5 sm:p-6">
              <div className="flex flex-col justify-between gap-5 lg:flex-row">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3"><span className={`border px-2.5 py-1 text-[10px] font-black uppercase tracking-[1px] ${status.className}`}>{status.label}</span><span className="inline-flex items-center gap-2 text-sm font-bold text-primary-orange"><CalendarDays size={16} />{dateLabel(request.requestedDate)}</span></div>
                  <h2 className="mt-5 text-xl font-black text-white">{request.eventType}</h2>
                  <p className="mt-2 text-sm text-white/65"><strong className="text-white">{request.contactName}</strong>{request.guestCount ? ` · ${request.guestCount} invitados` : ''}</p>
                  <p className="mt-1 inline-flex items-center gap-2 text-sm text-white/55"><Phone size={14} />{request.contactPhone || request.contactEmail}</p>
                  {request.startTime && request.endTime ? <p className="mt-2 text-xs font-bold text-white/40">Horario solicitado: {request.startTime} - {request.endTime}</p> : <p className="mt-2 text-xs font-bold text-white/40">Solicita el día completo</p>}
                  {request.message ? <p className="mt-4 max-w-2xl whitespace-pre-line text-sm leading-relaxed text-white/55">{request.message}</p> : null}
                </div>
                <div className="flex shrink-0 flex-wrap content-start gap-2 lg:w-48">
                  {!['confirmed', 'declined'].includes(request.status) ? <>
                    <button type="button" disabled={isBusy} onClick={() => updateStatus(request._id, 'contacted')} className="h-10 w-full border border-white/15 px-3 text-xs font-bold text-white/70 hover:bg-white/5">En conversación</button>
                    <button type="button" disabled={isBusy} onClick={() => updateStatus(request._id, 'quoted')} className="h-10 w-full border border-white/15 px-3 text-xs font-bold text-white/70 hover:bg-white/5">Presupuesto enviado</button>
                    <button type="button" disabled={isBusy} onClick={() => convert(request)} className="inline-flex h-10 w-full items-center justify-center gap-2 bg-emerald-500 px-3 text-xs font-black text-white hover:bg-emerald-400"><CheckCircle2 size={16} />Crear reserva</button>
                    <button type="button" disabled={isBusy} onClick={() => updateStatus(request._id, 'declined')} className="inline-flex h-10 w-full items-center justify-center gap-2 border border-red-400/25 px-3 text-xs font-bold text-red-300 hover:bg-red-500/10"><XCircle size={16} />Descartar</button>
                  </> : request.convertedEventId ? <button type="button" onClick={() => navigate(`/${slug}/admin/events/edit/${request.convertedEventId}`)} className="h-10 w-full border border-emerald-400/25 px-3 text-xs font-bold text-emerald-300 hover:bg-emerald-500/10">Ver reserva</button> : null}
                </div>
              </div>
            </article>;
          })}
        </div>
      </div>
    </div>
  );
}
